import React, { useCallback, useEffect, useMemo, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";

import useControlPanel from "src/hooks/useControlPanel";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import Pagination from "src/components/Pagination/Pagination";
import ReleaseTypeSpeakerControlPanel from "../../components/ControlPanel/ReleaseTypeSpeakerControlPanel";
import { dataStateType } from "src/constants/status";
import ReleaseTypeSpeakerTable from "../../components/Table/ReleaseTypeSpeakerTable";
import {
  createReleaseTypeSpeaker,
  getReleaseTypeSpeaker,
  sortReleaseTypeSpeaker,
  updateReleaseTypeSpeaker,
} from "src/apis/VersionRelease/typeSpeaker.api";
import { CACHE_RELEASE_TYPE_SPEAKER } from "src/constants/actionCache";
import { compareTwoArrayDnD } from "src/utils/helper";
import { NotifyConfirm } from "src/components/notify/Notify";
import { path } from "src/constants/path";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";

const initReleaseTypeSpeakerData = {
  list: [],
  total_record: 0,
};

const titlePage = "Speaker Type";

const ReleaseTypeSpeaker = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();

  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();

  const [releaseTypeSpeakerData, setReleaseTypeSpeakerData] = useState(
    initReleaseTypeSpeakerData
  );

  const [filterTypeSpeakers, setFilterTypeSpeakers] = useState([]);
  const [listTypeSpeakerId, setListTypeSpeakerId] = useState([]);
  const [queueSortApi, setQueueSortApi] = useState([]);
  const [isSorted, setIsSorted] = useState(false);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);

  const [errorsForm, setErrorsForm] = useState({});

  const fetchTypeSpeaker = useCallback(() => {
    getReleaseTypeSpeaker({}, (result) => {
      setReleaseTypeSpeakerData({ ...result });
      setFilterTypeSpeakers([...result.list]);
      setListTypeSpeakerId([...result.list?.map((c) => c.id)]);
    });
  }, []);

  useEffect(() => {
    fetchTypeSpeaker();
  }, [fetchTypeSpeaker]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  const updateFilteredReleaseTypeSpeaker = useCallback((typeSpeakers) => {
    setFilterTypeSpeakers(typeSpeakers);
    setListTypeSpeakerId(typeSpeakers.map((typeSpeaker) => typeSpeaker.id));
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (typeSpeakers, key) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return typeSpeakers.filter((typeSpeaker) =>
        typeSpeaker.name.toLowerCase().includes(key.toLowerCase().trim())
      );
    },
    [status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredReleaseTypeSpeaker = value
        ? searchByKeyWord(releaseTypeSpeakerData.list, value)
        : releaseTypeSpeakerData.list;
      updateFilteredReleaseTypeSpeaker(filteredReleaseTypeSpeaker);
    },
    [
      releaseTypeSpeakerData.list,
      searchByKeyWord,
      updateFilteredReleaseTypeSpeaker,
    ]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle add new and update ----- */
  const addOrEdit = useCallback(
    (typeSpeaker, resetForm) => {
      if (typeSpeaker.id) {
        updateReleaseTypeSpeaker(
          typeSpeaker.id,
          typeSpeaker,
          handleApiResponse
        );
      } else {
        createReleaseTypeSpeaker(typeSpeaker, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchTypeSpeaker();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_RELEASE_TYPE_SPEAKER);
        }
      }
    },
    [fetchTypeSpeaker, setCurrentActionCache]
  );

  /* ---- handle sort and DnD ----- */

  const handleSort = useCallback(() => {
    sortReleaseTypeSpeaker({ queueSort: queueSortApi }, () => {
      setQueueSortApi([]);
      setIsSorted(false);
      setCurrentActionCache(CACHE_RELEASE_TYPE_SPEAKER);
      fetchTypeSpeaker();
    });
  }, [fetchTypeSpeaker, queueSortApi, setCurrentActionCache]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // kiểm tra nếu không tồn tại over (kéo quá size thì return tránh lỗi)
    if (!over) return;

    if (active.id !== over.id) {
      // lấy vị trí cũ (từ thằng active)
      const oldIndex = filterTypeSpeakers.findIndex((c) => c.id === active.id);

      // lấy vị trí mới (từ thằng order)
      const newIndex = filterTypeSpeakers.findIndex((c) => c.id === over.id);

      const newOrder = arrayMove(filterTypeSpeakers, oldIndex, newIndex);
      const newOrderIds = newOrder.map((c) => c.id);

      // check xem có thay đổi thì hiện nút xác nhận cập nhật
      const isOrderChanged = !compareTwoArrayDnD(
        listTypeSpeakerId,
        newOrderIds
      );
      setFilterTypeSpeakers([...newOrder]);
      if (isOrderChanged) {
        setIsSorted(true);
        setQueueSortApi((prev) => [
          ...prev,
          {
            activeTypeSpeakerId: active.id,
            overTypeSpeakerId: over.id,
          },
        ]);
      } else {
        setIsSorted(false);
        setQueueSortApi([]);
      }
    }
  };

  const handleDelete = (typeSpeakerDeleted) => {
    NotifyConfirm("warning", "Are you delete it ?", (result) => {
      if (result.isConfirmed) {
        console.log("Implement delete function", typeSpeakerDeleted);
      }
    });
  };

  /* ---- result for render ----- */
  const resultFilter = useMemo(
    () => searchByKeyWord(filterTypeSpeakers, searchKey),
    [filterTypeSpeakers, searchByKeyWord, searchKey]
  );

  const listItems = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>{titlePage}</h2>
      </div>

      {/** control pan */}
      <ReleaseTypeSpeakerControlPanel
        handleOpenPopup={handleOpenPopup}
        isSorted={isSorted}
        handleSort={handleSort}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={false}
        />
      </ReleaseTypeSpeakerControlPanel>

      {/** table */}
      <ReleaseTypeSpeakerTable
        data={listItems}
        handleOpenPopup={handleOpenPopup}
        handleDelete={handleDelete}
        status={dataStateType.ACTIVE}
        onDragEnd={handleDragEnd}
      />

      <Pagination
        pathname={path.releaseAppVersion}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default ReleaseTypeSpeaker;
